import { useContext, useEffect, useState } from 'react'

import { AppStateContext } from '../../../state/AppProvider'

import { TextField } from '@mui/material'
import Grid from '@mui/material/Grid2'
import { first, isEmpty, isNull } from 'lodash'
import { NumericFormat } from 'react-number-format'
import { ChangeSourceEnum, FormField, updateExperimentFormField } from '../../../api'
import { ToastMessage } from '../../ExperimentForm/ExperimentFormToast'
import { FormFieldLabel } from '../FormFieldLabel'
import styles from './NumberFormField.module.css'

interface Props {
  name: string
  stepName: string
  fieldName: string
  convId: string
  data: FormField<number>
  gridSize: number
  onError?: (message: Pick<ToastMessage, 'title' | 'description' | 'type'>) => void
  disabled?: boolean
}

export const NumberFormField = ({ name, stepName, fieldName, convId, data, gridSize, onError, disabled }: Props) => {
  const appStateContext = useContext(AppStateContext)
  const [value, setValue] = useState(data?.field_value ?? 0)

  async function handleChange() {
    try {
      const newConversation = await updateExperimentFormField(
        stepName,
        fieldName,
        value,
        convId,
        appStateContext?.state.shouldValidateForm ?? false,
        appStateContext?.state.frontendSettings || undefined,
      )

      if (newConversation) {
        const messages = appStateContext?.state.currentChat?.messages || []
        newConversation.messages = messages

        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: newConversation })
      }
    } catch (error) {
      const errorWithReactElement = error as Error & { reactElement?: React.ReactNode }
      onError?.({
        title: 'Error',
        description:
          errorWithReactElement?.reactElement ||
          (error instanceof Error ? error.message : 'An error occurred while updating the field'),
        type: 'error',
      })
    }
  }

  useEffect(() => {
    setValue(data?.field_value ?? 0)
  }, [data?.field_value])

  return (
    <Grid
      size={gridSize}
      id={`field_${fieldName}`}>
      <FormFieldLabel
        text={name}
        isAiGenerated={data?.change_source !== ChangeSourceEnum.MANUAL}
        linkTitle="Convert"
        url={appStateContext?.state.frontendSettings?.template?.experiment_budget}
      />

      <NumericFormat
        error={!isEmpty(data.errors)}
        helperText={first(data.errors)}
        value={value}
        className={styles.textField}
        onValueChange={({ floatValue }) => {
          setValue(floatValue ?? 0)
        }}
        slotProps={{
          inputLabel: { shrink: false },
          input: { classes: { disabled: styles.disabledTextField, error: styles.errorInput } },
        }}
        fullWidth
        onBlur={handleChange}
        customInput={TextField}
        thousandSeparator
        valueIsNumericString
        prefix="$"
        variant="outlined"
        label={isNull(value) && isEmpty(value) && `Enter ${name}`}
        disabled={disabled}
      />
    </Grid>
  )
}
